import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NavController } from '@ionic/angular/standalone';
import { TranslatePipe, _ } from '@ngx-translate/core';
import { filter, forkJoin, take } from 'rxjs';
import { AddressCardComponent } from 'src/app/components/address-card/address-card.component';
import { DeciHelperComponent } from 'src/app/components/deci-helper/deci-helper.component';
import { LoadingUtils } from 'src/app/utils/loading.utils';
import { PostShipment } from '../../../../models/delivery';
import { Order } from '../../../../models/order';
import { Address, User } from '../../../../models/user';
import { AddressService } from '../../../../services/address.service';
import { AuthService } from '../../../../services/auth.service';
import { CompanyService } from '../../../../services/company.service';
import { OrderService } from '../../../../services/order.service';
import { ShippingService } from '../../../../services/shipping.service';
import { ToastService } from '../../../../services/toast.service';
import { RegexUtils } from '../../../../utils/regex.utils';
import { resolveShipmentReturnAddress, shipmentReturnAddressBadges, shipmentReturnAddressStrategy } from './shipment-return-address';

@Component({
    selector: 'app-profile-tab-order-shipment',
    templateUrl: './profile-tab-order-shipment.component.html',
    styleUrls: ['./profile-tab-order-shipment.component.scss'],
    imports: [CommonModule, TranslatePipe, FormsModule, ReactiveFormsModule, AddressCardComponent, DeciHelperComponent],
})
export class ProfileTabOrderShipmentComponent implements OnInit {
    order: Order;
    form: FormGroup;
    accountType: User['type'] = 'personal';
    returnAddress: Address | null = null;
    returnAddressLoaded = false;

    constructor(
        private readonly orderService: OrderService,
        private readonly route: ActivatedRoute,
        private readonly navCtrl: NavController,
        private readonly toastService: ToastService,
        private readonly shippingService: ShippingService,
        private readonly authService: AuthService,
        private readonly companyService: CompanyService,
        private readonly addressService: AddressService,
    ) {}

    ngOnInit() {
        const ref = this.route.snapshot.paramMap.get('ref');
        forkJoin({
            user: this.authService.userLoggedIn.pipe(
                filter((user) => !!user),
                take(1),
            ),
            order: this.orderService.getOrder(ref),
        }).subscribe({
            next: ({ order, user }) => {
                this.order = order;
                this.accountType = user.type;
                this.initForm();
                this.loadReturnAddress(user);
            },
        });
    }

    get returnAddressBadges(): Array<'return' | 'default'> {
        return shipmentReturnAddressBadges(this.accountType);
    }

    private loadReturnAddress(user: User): void {
        const strategy = shipmentReturnAddressStrategy(user);
        switch (strategy) {
            case 'company':
                this.companyService.getAddresses(user.company).subscribe({
                    next: (addresses) => {
                        this.returnAddress = resolveShipmentReturnAddress({
                            user,
                            companyReturnAddress: addresses?.return,
                        });
                        this.returnAddressLoaded = true;
                    },
                    error: () => {
                        this.returnAddress = null;
                        this.returnAddressLoaded = true;
                    },
                });
                break;
            case 'default':
                this.returnAddress = resolveShipmentReturnAddress({ user });
                this.returnAddressLoaded = true;
                break;
            case 'profile':
                this.addressService.getAddresses(user.id).subscribe({
                    next: (addresses) => {
                        this.returnAddress = resolveShipmentReturnAddress({
                            user,
                            profileAddresses: addresses,
                        });
                        this.returnAddressLoaded = true;
                    },
                    error: () => {
                        this.returnAddress = null;
                        this.returnAddressLoaded = true;
                    },
                });
                break;
            default: {
                const exhaustive: never = strategy;
                return exhaustive;
            }
        }
    }

    initForm() {
        this.form = new FormGroup({
            shipments: new FormArray([], [Validators.required, Validators.minLength(1)]),
        });
    }

    get canSubmit(): boolean {
        return !!this.form && this.form.valid && !!this.returnAddress;
    }

    back() {
        this.navCtrl.back();
    }

    goToAddresses() {
        this.navCtrl.navigateForward('/profile/addresses');
    }

    async submit() {
        if (!this.canSubmit) {
            return;
        }
        const shipment: PostShipment = {
            shipments: this.form.value.shipments,
        };
        await LoadingUtils.show();
        this.shippingService.createShipment(this.order, shipment).subscribe({
            next: () => {
                this.navCtrl.back();
                LoadingUtils.hide();
                this.toastService.success(_('Shipment created'));
            },
            error: (response) => {
                LoadingUtils.hide();
                const message = response?.error?.error || _('An error occurred');
                this.toastService.error(message);
            },
        });
    }

    addShipmentForm() {
        (this.form.get('shipments') as FormArray).push(
            new FormGroup({
                weight: new FormControl('', [Validators.required, Validators.pattern(RegexUtils.integer)]),
                deci: new FormControl('', [Validators.required, Validators.pattern(RegexUtils.decimal)]),
            }),
        );
    }

    removeShipmentForm(index: number) {
        (this.form.get('shipments') as FormArray).removeAt(index);
    }

    getShipmentForms(): FormGroup[] {
        return (this.form.get('shipments') as FormArray).controls as FormGroup[];
    }
}
